'use client';

import React from 'react';
import AppLayout from '@/components/AppLayout';
import Link from 'next/link';
import { Cookie, ChevronRight } from 'lucide-react';

const sections = [
  {
    id: 'what-are-cookies',
    title: '1. What Are Cookies?',
    content: `Cookies are small text files placed on your device (computer, tablet, or mobile) when you visit a website. They are widely used to make websites work efficiently, remember your preferences, and provide information to website owners.

Cookies do not contain personally identifiable information on their own, but they may be linked to personal data we hold about you. We use cookies and similar technologies such as web beacons, pixels, and local storage.`,
  },
  {
    id: 'types',
    title: '2. Types of Cookies We Use',
    content: `**Essential Cookies**
These cookies are strictly necessary for the Platform to function. Without them, services like authentication, session management, and security cannot be provided. You cannot opt out of essential cookies.

Examples:
• Authentication session tokens (keeping you logged in)
• Security cookies (CSRF protection)
• Load balancing cookies

**Preference Cookies**
These cookies remember your choices and personalise your experience on the Platform.

Examples:
• Language and region preferences
• Reading history and saved collections
• Display settings and layout preferences

**Analytics Cookies**
These cookies help us understand how visitors interact with the Platform, which pages are most visited, and how users navigate. This data is used in aggregate to improve our content and user experience.

Examples:
• Google Analytics (page views, session duration, traffic sources)
• Internal usage metrics

**Marketing & Personalisation Cookies**
These cookies may be used to deliver content and editorial recommendations more relevant to your interests. They may also be used to measure the effectiveness of our communications.`,
  },
  {
    id: 'third-party',
    title: '3. Third-Party Cookies',
    content: `Some cookies on our Platform are set by third-party services we use. These third parties may collect information about your online activities across different websites. We do not control these third-party cookies and recommend reviewing the privacy policies of these providers:

• **Google Analytics**: Analytics and usage tracking — policies.google.com/privacy
• **Stripe**: Payment processing and fraud prevention — stripe.com/privacy
• **Supabase**: Authentication and database services — supabase.com/privacy

We only work with third parties who provide adequate data protection guarantees.`,
  },
  {
    id: 'duration',
    title: '4. Cookie Duration',
    content: `Cookies can be either session cookies or persistent cookies:

• **Session Cookies**: Temporary cookies that expire when you close your browser. Used for authentication and security during your visit.

• **Persistent Cookies**: Remain on your device for a set period (days, months, or years) or until you delete them. Used for preferences, analytics, and personalisation.

Most of our analytics cookies expire within 13 months. Authentication cookies expire when you sign out or after a period of inactivity.`,
  },
  {
    id: 'managing',
    title: '5. Managing Your Cookie Preferences',
    content: `You have several options for managing cookies:

**Browser Settings**
Most browsers allow you to control cookies through their settings. You can:
• View cookies stored on your device
• Delete all or specific cookies
• Block cookies from all or specific websites
• Set preferences for future cookies

Common browser cookie settings:
• Chrome: Settings → Privacy and Security → Cookies
• Safari: Preferences → Privacy → Cookies
• Firefox: Options → Privacy & Security → Cookies
• Edge: Settings → Privacy, Search and Services → Cookies

**Opt-Out Tools**
• Google Analytics opt-out: tools.google.com/dlpage/gaoptout
• Your Online Choices (EU): youronlinechoices.eu

**Impact of Disabling Cookies**
Disabling essential cookies will prevent you from logging in and using member features. Disabling preference cookies will mean your settings are not remembered between visits. Disabling analytics cookies will not affect your ability to use the Platform.`,
  },
  {
    id: 'local-storage',
    title: '6. Local Storage & Similar Technologies',
    content: `In addition to cookies, we may use browser local storage and session storage to store small amounts of data on your device. This data is used for:

• Caching editorial content for faster loading
• Storing UI preferences and reading progress
• Maintaining application state between page navigations

You can clear local storage data through your browser's developer tools or by clearing your browser's site data.`,
  },
  {
    id: 'updates',
    title: '7. Updates to This Policy',
    content: `We may update this Cookie Policy from time to time to reflect changes in technology, regulation, or our practices. We will post the updated policy on this page with a revised effective date. We encourage you to review this policy periodically.`,
  },
  {
    id: 'contact',
    title: '8. Contact Us',
    content: `If you have questions about our use of cookies or this Cookie Policy, please contact us:

**Silvergate Royals**
Registered Address: New York, United States
Email: privacy@theprivateworld.app
Website: theprivateworld.app`,
  },
];

export default function CookiePolicyPage() {
  return (
    <AppLayout currentPath="/cookie-policy">
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="border-b border-[var(--border)] bg-[var(--secondary)]">
          <div className="max-w-3xl mx-auto px-6 py-10">
            <div className="flex items-center gap-2 text-xs text-[var(--slate-muted)] mb-4">
              <Link href="/" className="hover:text-[var(--gold)] transition-colors">Home</Link>
              <ChevronRight size={12} />
              <span>Cookie Policy</span>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/20 flex items-center justify-center">
                <Cookie size={16} className="text-[var(--gold)]" />
              </div>
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[var(--gold)]">Legal</span>
            </div>
            <h1 className="font-editorial text-3xl font-semibold text-[var(--foreground)] mb-2">Cookie Policy</h1>
            <p className="text-sm text-[var(--slate-muted)]">Effective date: 1 September 2025 &nbsp;·&nbsp; Last updated: 1 September 2025</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-10">
          {/* Quick Nav */}
          <div className="mb-10 p-5 bg-[var(--secondary)] border border-[var(--border)] rounded">
            <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-[var(--slate-muted)] mb-3">Contents</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {sections?.map((s) => (
                <a
                  key={s?.id}
                  href={`#${s?.id}`}
                  className="text-xs text-[var(--slate-muted)] hover:text-[var(--gold)] transition-colors py-0.5"
                >
                  {s?.title}
                </a>
              ))}
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {sections?.map((section) => (
              <section key={section?.id} id={section?.id} className="scroll-mt-6">
                <h2 className="font-editorial text-lg font-semibold text-[var(--foreground)] mb-3 pb-2 border-b border-[var(--border)]">
                  {section?.title}
                </h2>
                <div className="text-sm text-[var(--slate-muted)] leading-relaxed">
                  {section?.content?.split('\n')?.map((line, i) => {
                    if (line?.startsWith('• ')) {
                      return <p key={i} className="mb-1.5 pl-3">{line}</p>;
                    }
                    if (line?.startsWith('**') && line?.endsWith('**')) {
                      return <p key={i} className="font-semibold text-[var(--foreground)] mt-4 mb-1.5">{line?.replace(/\*\*/g, '')}</p>;
                    }
                    if (line === '') return <div key={i} className="h-2" />;
                    return <p key={i} className="mb-2">{line}</p>;
                  })}
                </div>
              </section>
            ))}
          </div>

          {/* Footer Links */}
          <div className="mt-14 pt-6 border-t border-[var(--border)] flex flex-wrap gap-4 text-xs text-[var(--slate-muted)]">
            <Link href="/privacy-policy" className="hover:text-[var(--gold)] transition-colors">Privacy Policy</Link>
            <Link href="/terms-of-service" className="hover:text-[var(--gold)] transition-colors">Terms of Service</Link>
            <Link href="/" className="hover:text-[var(--gold)] transition-colors">Back to Home</Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
